// ALL COPY IN THIS MODULE IS PROVISIONAL pending the final design states pass.
// Every user-facing string for the formula asset cells lives here and only here.
// Notes spell numbers out ("add three more assets"); tooltips use digits ("4 of 5"), bold the
// counts, and close every success state with "Great job!".
import * as React from 'react';

interface MetricCopy {
  notes: React.ReactNode;
  tooltip: React.ReactNode;
}

const NUMBER_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine'];
const numberWord = (n: number) => NUMBER_WORDS[n] ?? String(n);

/**
 * Renders a comma-separated (Oxford-comma-less "and" before the last entry) list of
 * missing asset class names, bolding each name and appending " (n)" when more than
 * one asset of that class is missing.
 */
const classList = (classes: Array<{ name: string; count?: number }>): React.ReactNode =>
  classes.map((entry, index) => (
    <React.Fragment key={entry.name}>
      {index > 0 && (index === classes.length - 1 ? ' and ' : ', ')}
      <strong>{entry.name}</strong>
      {entry.count != null && entry.count > 1 ? ` (${entry.count})` : null}
    </React.Fragment>
  ));

const assetMixCopy = ({
  missing,
  required,
  missingClasses,
}: {
  missing: number;
  required: number;
  missingClasses?: Array<{ name: string; count?: number }>;
}): MetricCopy => {
  if (missing === 0) {
    return {
      notes: 'Strong mix!',
      tooltip: (
        <>
          You have all <strong>{required}</strong> optimal asset classes. Great job!
        </>
      ),
    };
  }

  const notes = (
    <>
      <strong>add</strong> {numberWord(missing)} missing {missing === 1 ? 'class' : 'classes'}
    </>
  );

  if (missingClasses != null && missingClasses.length > 0) {
    return {
      notes,
      tooltip: (
        <>
          You have{' '}
          <strong>
            {required - missing} of {required}
          </strong>{' '}
          optimal asset classes. Add a {classList(missingClasses)}
          {' asset to score 100.'}
        </>
      ),
    };
  }

  return {
    notes,
    tooltip: (
      <>
        You have{' '}
        <strong>
          {required - missing} of {required}
        </strong>{' '}
        optimal asset classes. Add {missing} missing {missing === 1 ? 'class' : 'classes'}
        {' to score 100.'}
      </>
    ),
  };
};

const assetCountCopy = ({
  count,
  optimalMin,
  optimalMax,
}: {
  count: number;
  optimalMin: number;
  optimalMax: number;
}): MetricCopy => {
  // Formula's image count component carries an exact `required` target (see
  // ImageCountDetail) — being over is penalized just like being short — so its caller
  // maps optimalMin and optimalMax to the same value. GAM supplies a real band
  // (optimalMin < optimalMax); this module can't tell the two callers apart except by
  // that equality, but both treat counts above the top the same way: remove the excess.
  // The current count and the optimal count are bold, like the asset mix tip's "3 of 5".
  const optimalCount = optimalMin === optimalMax ? optimalMin : `${optimalMin}-${optimalMax}`;
  const optimal = (
    <>
      The optimal asset count is <strong>{optimalCount}</strong>
    </>
  );
  if (count < optimalMin) {
    const shortBy = optimalMin - count;
    return {
      notes: (
        <>
          <strong>add</strong> {numberWord(shortBy)} more {shortBy === 1 ? 'asset' : 'assets'}
        </>
      ),
      tooltip: (
        <>
          You have <strong>{count}</strong> assets. {optimal}. Add {shortBy} {shortBy === 1 ? 'asset' : 'assets'}
          {' to score 100.'}
        </>
      ),
    };
  }
  if (count > optimalMax) {
    const overBy = count - optimalMax;
    return {
      notes: (
        <>
          <strong>remove</strong> {numberWord(overBy)} {overBy === 1 ? 'more asset' : 'more assets'}
        </>
      ),
      tooltip: (
        <>
          You have <strong>{count}</strong> assets. {optimal}. Remove {overBy} {overBy === 1 ? 'asset' : 'assets'}
          {' to score 100.'}
        </>
      ),
    };
  }
  return {
    notes: 'Strong count!',
    tooltip: (
      <>
        You have the optimal asset count of <strong>{count}</strong>. Great job!
      </>
    ),
  };
};

const assetOrderCopy = ({ isSorted }: { isSorted: boolean }): MetricCopy =>
  isSorted
    ? { notes: 'Strong order!', tooltip: 'Carousel assets are sorted from highest to lowest score. Great job!' }
    : { notes: 'assets are out of order', tooltip: 'Sort carousel assets from highest to lowest score to score 100.' };

export { assetCountCopy, assetMixCopy, assetOrderCopy };
export type { MetricCopy };
